import PropTypes from 'prop-types';
/**
 * A wrapper component that adds tooltip behaviours to its wrapped children element.
 * It supports smart tooltip repositioning by default.
 *
 * Example:
 * <p>Hello <Tooltip tooltip="earth">world</Tooltip></p>
 *
 * var complicatedEarth = <div>EARTH</div>
 * <p>Hello <Tooltip tooltipElement={complicatedEarth}>world</Tooltip></p>
 */

import React from 'react';
import HoverableElement from './HoverableElement';
import * as elementPosition from '../lib/elementPosition';
import keys from '../lib/keyConstants';
import uniqueId from 'lodash.uniqueid';

export default class Tooltip extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isActive: false,
        };
        this.activate = this.activate.bind(this);
        this.deactivate = this.deactivate.bind(this);
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    activate() {
        this.setState({ isActive: true });
    }

    deactivate() {
        this.setState({ isActive: false });
    }

    onKeyDown(event) {
        if (event.key === keys.ESCAPE && this.state.isActive) {
            event.preventDefault();
            event.stopPropagation();
            this.deactivate();
        }
    }

    getHoverElement() {
        var id = uniqueId('tooltip_');
        var hoverElement;

        if (this.props.tooltipElement) {
            hoverElement = React.cloneElement(this.props.tooltipElement, {
                role: 'tooltip',
                id: id,
                'aria-hidden': !this.state.isActive,
            });
        } else {
            hoverElement = (
                <span
                    className="hover-card__content__inner"
                    id={id}
                    role="tooltip"
                    aria-hidden={!this.state.isActive}
                >
                    {this.props.tooltip}
                </span>
            );
        }

        return hoverElement;
    }

    handleMouseEnter() {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(this.activate, 200);
    }

    handleMouseLeave() {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(this.deactivate, 200);
    }

    render() {
        var hoverElement = this.getHoverElement();

        return (
            <HoverableElement
                hoverElement={hoverElement}
                hoverPosition={this.props.position}
                isActive={this.state.isActive}
                type="tooltip"
                hasTransition={this.props.hasTransition}
                variations={this.props.hoverableElementVariations}
                hoverCardVariations={this.props.hoverCardVariations}
            >
                <span
                    data-tooltip-trigger-element
                    onMouseEnter={this.handleMouseEnter}
                    onMouseLeave={this.handleMouseLeave}
                    onFocus={this.activate}
                    onBlur={this.deactivate}
                    onKeyDown={this.onKeyDown}
                    aria-describedby={(this.state.isActive && hoverElement.props.id) || null}
                >
                    {this.props.children}
                </span>
            </HoverableElement>
        );
    }
}

Tooltip.propTypes = {
    position: PropTypes.oneOf(elementPosition.getPossiblePositions()),
    tooltip: PropTypes.node,
    tooltipElement: PropTypes.element,
    hoverCardVariations: PropTypes.array,
    hoverableElementVariations: PropTypes.array,
    hasTransition: PropTypes.bool,
};

Tooltip.defaultProps = {
    position: 'down-center',
    tooltip: '',
    hoverCardVariations: [],
    hoverableElementVariations: [],
    hasTransition: true,
};



// WEBPACK FOOTER //
// ./~/@99designs/react-components/components/Tooltip.jsx